import { useEffect, useRef } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

import { type OptionItemCountry, useGetCityByCountrysQuery } from "@/api/admin";
import { SelectControl } from "@/components/molecules";

interface CountryCitySelectProps<TFormValues extends FieldValues> {
  form: UseFormReturn<TFormValues>;
  countries: OptionItemCountry[];
}

export function CountryCitySelect<TFormValues extends FieldValues>({
  form,
  countries,
}: CountryCitySelectProps<TFormValues>) {
  const { watch, setValue, trigger } = form;

  const selectedCountryName = watch("country");

  const countryCode = countries.find(
    country => country.name === selectedCountryName
  )?.code;

  const { data: citiesData, isPending: isCitiesPending } =
    useGetCityByCountrysQuery(countryCode ?? "");

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      setValue("cityId", "");
      trigger("cityId");
    }
  }, [selectedCountryName]);

  return (
    <div className="flex align-center gap-x-0 gap-y-4 sm:gap-x-4 sm:gap-y-0 mb-4.5 flex-col sm:flex-row">
      <SelectControl<TFormValues>
        form={form}
        nameSelect="country"
        label="Country"
        placeholder="Select"
        itemsList={countries}
        triggerProps={{
          "data-testid": "new-legal-entity-form-country",
        }}
      />

      <SelectControl<TFormValues>
        form={form}
        nameSelect="cityId"
        label="City"
        placeholder={countryCode ? "Select city" : "Select country first"}
        itemsList={citiesData || []}
        disabled={!countryCode || isCitiesPending}
        triggerProps={{
          "data-testid": "new-legal-entity-form-city",
        }}
      />
    </div>
  );
}
